export default function getLookupTree(
  labelData,
  partnerData,
  PartnerDataResultObject,
  markaazMatchResult,
  advanceMatchingResult
) {
  const overview = getOverview(
    partnerData,
    labelData,
    PartnerDataResultObject,
    markaazMatchResult,
    advanceMatchingResult
  );
  const firmographic = getFirmographic(partnerData, labelData);
  const health = getBusinessHealth(partnerData, labelData);
  const compliance = getCompliance(partnerData, labelData);
  const hierarchy = getHierarchy(partnerData, labelData);
  const diversity = getDiversity(partnerData, labelData);
  const nameMatching = getNameMatching(partnerData, labelData);

  return {
    overview,
    firmographic,
    health,
    compliance,
    hierarchy,
    diversity,
    nameMatching
  };
}

function getOverview(partnerData, mappingData, PartnerDataResultObject, markaazMatchResult, advanceMatchingResult) {
  const labels = mappingData.bizOverview;
  const contactLabel = mappingData.bizFirmographic;
  const values = markaazMatchResult.bizInfo || PartnerDataResultObject.bizInfo;
  const metaData = markaazMatchResult.metaData || null;
  const primaryContact = markaazMatchResult.primaryContact;

  if (!values) {
    return {
      data: null,
      message: "None."
    };
  }

  const overview = {
    [`${labels.traceId}`]: `"${advanceMatchingResult.traceId}"`,
    [`${labels.markaazId}`]: markaazMatchResult.markaazId || PartnerDataResultObject.markaazId,
    [`${labels.markaazMatchConfidenceScore}`]: markaazMatchResult.markaazMatchConfidenceScore,
    [`${labels.markaazMatchConfidenceThreshold}`]: markaazMatchResult.matchConfidenceThreshold,
    [`${labels.dba}`]: values.dba,
    [`${labels.legalName}`]: values.legalName,
    [`${labels.taxId}`]: values.taxId,
    [`${labels.nationalId}`]: values.nationalId,
    [`${labels.website}`]: values.website,
    [`${labels.phoneNumber}`]: values.phoneNumber,
    [`${labels.addresses}`]: [],
    [`${contactLabel.primaryContact}`]: {
      [`${contactLabel.primaryContact_fullName}`]: primaryContact.fullName,
      [`${contactLabel.primaryContact_titleCode}`]: primaryContact.titleCode,
      [`${contactLabel.primaryContact_title}`]: primaryContact.title,
      [`${contactLabel.primaryContact_firstName}`]: primaryContact.firstName,
      [`${contactLabel.primaryContact_lastName}`]: primaryContact.lastName,
      [`${contactLabel.primaryContact_email}`]: primaryContact.email
    }
  };

  if (metaData) {
    overview[`${labels?.metaData}`] = {
      [`${labels.dba}`]: metaData?.dba?.grade || null,
      [`${labels.legalName}`]: metaData?.companyLegalName?.grade || null,
      [`${labels.address}`]: metaData?.address?.grade || null,
      [`${labels.locality}`]: metaData?.locality?.grade || null,
      [`${labels.region}`]: metaData?.region?.grade || null,
      [`${labels.postalCode}`]: metaData?.postalCode?.grade || null,
      [`${labels.nationalId}`]: metaData?.nationalId?.grade || null,
      [`${labels.taxId}`]: metaData?.taxId?.grade || null,
      [`${labels.phoneNumber}`]: metaData?.phoneNumber?.grade || null,
      [`${labels.website}`]: metaData?.website?.grade || null,
      [`${labels.contactName}`]: metaData?.contactName?.grade || null
    };
  }

  values.addresses.forEach((address) => {
    const newAddress = {
      _label: `${labels.type} ${address.type}`,
      [`${labels.type}`]: address.type,
      [`${labels.line1}`]: address.line1,
      [`${labels.line2}`]: address.line2,
      [`${labels.locality}`]: address.locality,
      [`${labels.region}`]: address.region,
      [`${labels.country}`]: address.country,
      [`${labels.postalCode}`]: address.postalCode,
      [`${labels.latitude}`]: address.latitude,
      [`${labels.longitude}`]: address.longitude,
      [`${labels.geoprecision}`]: address.geoprecision,
      [`${labels.businessIndicator}`]: address.businessIndicator,
      [`${labels.residentialIndicator}`]: address.residentialIndicator,
      [`${labels.sohoIndicator}`]: address.sohoIndicator
    };

    overview[`${labels.addresses}`].push(newAddress);
  });
  return { data: overview };
}

function getFirmographic(partnerData, mappingData) {
  const traceIdLabel = mappingData.bizOverview.traceId;
  const labels = mappingData.bizFirmographic;
  const values = partnerData.bizFirmographic?.firmographic;

  if (!values) {
    return {
      data: null,
      message: "None."
    };
  }

  const firmographic = {
    [`${traceIdLabel}`]: `"${partnerData.bizFirmographic.traceId}"`,
    [`${labels.yearEstablished}`]: values.yearEstablished,
    [`${labels.legalStructure}`]: values.legalStructure,
    [`${labels.companyStatus}`]: values.companyStatus,
    [`${labels.registrationDate}`]: values.registrationDate,
    [`${labels.noOfEmployees}`]: values.noOfEmployees,
    [`${labels.noOfEmployeesLoc}`]: values.noOfEmployeesLoc,
    [`${labels.salesVolume}`]: values.salesVolume,
    [`${labels.salesVolumePrecision}`]: values.salesVolumePrecision,
    [`${labels.salesVolumeLoc}`]: values.salesVolumeLoc,
    [`${labels.salesVolumeLocPrecision}`]: values.salesVolumeLocPrecision,
    [`${labels.primaryNaics}`]: values.primaryNaics,
    [`${labels.secondaryNaics1}`]: values.secondaryNaics1,
    [`${labels.secondaryNaics2}`]: values.secondaryNaics2,
    [`${labels.secondaryNaics3}`]: values.secondaryNaics3,
    [`${labels.secondaryNaics4}`]: values.secondaryNaics4,
    [`${labels.businessSize}`]: values.businessSize,
    [`${labels.contact}`]: {
      [`${labels.website}`]: values.contact.website,
      [`${labels.countryCallingCode}`]: values.contact.countryCallingCode,
      [`${labels.phone}`]: values.contact.phone,
      [`${labels.fax}`]: values.contact.fax
    },
    [`${labels.officers}`]: [],
    [`${labels.primaryContact}`]: {
      [`${labels.primaryContact_fullName}`]: values.primaryContact.fullName,
      [`${labels.primaryContact_titleCode}`]: values.primaryContact.titleCode,
      [`${labels.primaryContact_title}`]: values.primaryContact.title,
      [`${labels.primaryContact_firstName}`]: values.primaryContact.firstName,
      [`${labels.primaryContact_lastName}`]: values.primaryContact.lastName,
      [`${labels.primaryContact_email}`]: values.primaryContact.email
    },
    [`${labels.primaryContact_hasCompanyHierarchy}`]: values.metadata.hasCompanyHierarchy
  };

  values.officers.forEach((officer) => {
    const newOfficer = {
      _label: officer.fullName,
      [`${labels.officers_officerId}`]: officer.officerId,
      [`${labels.officers_fullName}`]: officer.fullName,
      [`${labels.officers_title}`]: officer.title,
      [`${labels.officers_position}`]: officer.position,
      [`${labels.officers_currentStatus}`]: officer.currentStatus
    };

    firmographic[`${labels.officers}`].push(newOfficer);
  });

  return { data: firmographic };
}

function getBusinessHealth(partnerData, mappingData) {
  const traceIdLabel = mappingData.bizOverview.traceId;
  const labels = mappingData.bizHealth;
  const values = partnerData.bizHealth?.businessHealth;

  if (!values) {
    return {
      data: null,
      message: "None."
    };
  }

  const health = {
    [`${traceIdLabel}`]: `"${partnerData.bizHealth.traceId}"`,
    [`${labels.bankruptcy}`]: values.bankruptcy,
    [`${labels.markaazFailureRiskScore}`]: values.markaazFailureRiskScore,
    [`${labels.markaazCreditRiskScore}`]: values.markaazCreditRiskScore,
    [`${labels.markaazGlobalCreditRiskScore}`]: values.markaazGlobalCreditRiskScore
  };

  return { data: health };
}

function getCompliance(partnerData, mappingData) {
  const traceIdLabel = mappingData.bizOverview.traceId;
  const labels = mappingData.bizCompliance;
  const values = partnerData.bizCompliance?.complianceSummary;
  const complianceDetails = partnerData.bizCompliance?.complianceDetails;

  if (!values) {
    return {
      data: null,
      message: null
    };
  }

  const compliance = {
    [`${traceIdLabel}`]: `"${partnerData.bizCompliance.traceId}"`,
    [`${labels.activeSanctions}`]: values.activeSanctions,
    [`${labels.activeInsolvency}`]: values.activeInsolvency,
    [`${labels.activePoliticallyExposedPersons}`]: values.activePoliticallyExposedPersons,
    [`${labels.activeAdverseMedia}`]: values.activeAdverseMedia,
    [`${labels.activeLawEnforcement}`]: values.activeLawEnforcement,
    _complianceDetails: {
      adverseMedia: complianceDetails.adverseMedia,
      evidences: complianceDetails.evidences,
      insolvency: complianceDetails.insolvency,
      lawEnforcement: complianceDetails.lawEnforcement,
      pep: complianceDetails.pep,
      sanctions: complianceDetails.sanctions
    }
  };

  return { data: compliance };
}

function getHierarchy(partnerData, mappingData) {
  const traceIdLabel = mappingData.bizOverview.traceId;
  const labels = mappingData.bizHierarchy;
  const values = partnerData.bizHierarchy?.companyHierarchy;

  if (!values) {
    return {
      data: null,
      message: "None."
    };
  }

  const hierarchy = {
    [`${traceIdLabel}`]: `"${partnerData.bizHierarchy.traceId}"`
  };

  const companyHierarchy = [];

  values.forEach((item) => {
    const newItem = {
      _label: item.companyName,
      [`${labels.parentMarkaazId}`]: item.parentMarkaazId,
      [`${labels.markaazId}`]: item.markaazId,
      [`${labels.companyLegalName}`]: item.companyLegalName,
      [`${labels.contact}`]: {
        [`${labels.phoneNumber}`]: item.contact.phoneNumber,
        [`${labels.countryCallingCode}`]: item.contact.countryCallingCode,
        [`${labels.website}`]: item.contact.website
      },
      [`${labels.parentTypeCode}`]: item.parentTypeCode,
      [`${labels.companyName}`]: item.companyName,
      [`${labels.addresses}`]: []
    };

    item.addresses.forEach((address) => {
      const newAddress = {
        _label: `${labels.type} ${address.type}`,
        [`${labels.type}`]: address.type,
        [`${labels.line1}`]: address.line1,
        [`${labels.line2}`]: address.line2,
        [`${labels.locality}`]: address.locality,
        [`${labels.region}`]: address.region,
        [`${labels.regionAbbreviation}`]: address.regionAbbreviation,
        [`${labels.country}`]: address.country,
        [`${labels.postalCode}`]: address.postalCode
      };

      newItem[`${labels.addresses}`].push(newAddress);
    });

    companyHierarchy.push(newItem);
  });

  hierarchy[`${labels.companyHierarchy}`] = companyHierarchy;

  return { data: hierarchy };
}

function getDiversity(partnerData, mappingData) {
  const traceIdLabel = mappingData.bizOverview.traceId;
  const labels = mappingData.bizDiversity;
  const values = partnerData.bizDiversity?.diversity;

  if (!values) {
    return {
      data: null,
      message: "None."
    };
  }

  const diversity = {
    [`${traceIdLabel}`]: `"${partnerData.bizDiversity.traceId}"`,
    [`${labels.minorityIndicator}`]: values.minorityIndicator,
    [`${labels.veteranOwnedIndicator}`]: values.veteranOwnedIndicator,
    [`${labels.womenOwnedIndicator}`]: values.womenOwnedIndicator
  };

  return { data: diversity };
}

function getNameMatching(partnerData, mappingData) {
  const traceIdLabel = mappingData.bizOverview.traceId;
  const labels = mappingData.officersNameMatching;

  const nameMatchResults = partnerData.officersNameMatching?.nameMatchResults;
  const officers = partnerData.officersNameMatching?.officers;
  const primaryContact = partnerData.officersNameMatching?.primaryContact;

  if (!nameMatchResults) {
    return {
      data: null,
      message: partnerData?.officersNameMatching?.message
    };
  }

  const nameMatching = {
    [`${traceIdLabel}`]: `"${partnerData.officersNameMatching.traceId}"`,
    [`${labels.nameMatchResults}`]: [],
    [`${labels.primaryContact}`]: {
      [`${labels.primaryContact_fullName}`]: primaryContact.fullName,
      [`${labels.primaryContact_titleCode}`]: primaryContact.titleCode,
      [`${labels.primaryContact_title}`]: primaryContact.title,
      [`${labels.primaryContact_firstName}`]: primaryContact.firstName,
      [`${labels.primaryContact_lastName}`]: primaryContact.lastName,
      [`${labels.primaryContact_email}`]: primaryContact.email
    },
    [`${labels.officers}`]: []
  };

  nameMatchResults.forEach((nameMatchResult) => {
    const nameMatched = {
      _label: nameMatchResult.nameMatchRequested,
      [`${labels.matched}`]: nameMatchResult.matched,
      [`${labels.message}`]: nameMatchResult.message,
      [`${labels.nameMatchRequested}`]: nameMatchResult.nameMatchRequested,
      [`${labels.nameMatched}`]: nameMatchResult.nameMatched,
      [`${labels.officerId}`]: nameMatchResult.officerId,
      [`${labels.matchGrade}`]: nameMatchResult.matchGrade
    };

    nameMatching[`${labels.nameMatchResults}`].push(nameMatched);
  });

  officers.forEach((officer) => {
    const newOfficer = {
      _label: officer.fullName,
      [`${labels.officers_officerId}`]: officer.officerId,
      [`${labels.officers_fullName}`]: officer.fullName,
      [`${labels.officers_title}`]: officer.title,
      [`${labels.officers_position}`]: officer.position,
      [`${labels.officers_currentStatus}`]: officer.currentStatus
    };

    nameMatching[`${labels.officers}`].push(newOfficer);
  });

  return { data: nameMatching };
}
